import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS: { key: string; label: string; hint?: string; url?: boolean }[] = [
  { key: "repositoryUrl", label: "Repository URL", url: true },
  { key: "commit", label: "Commit / tag" },
  { key: "environmentLink", label: "Environment or dependency file link", url: true },
  { key: "softwareVersions", label: "Software / instrument versions" },
  { key: "datasetId", label: "Input dataset identifier" },
  { key: "datasetVersion", label: "Dataset version" },
  { key: "datasetChecksum", label: "Dataset checksum (optional, not checked)" },
  { key: "parameters", label: "Parameters" },
  { key: "seed", label: "Random seed" },
  { key: "scriptLink", label: "Analysis script / notebook link", url: true },
  { key: "outputLinks", label: "Links to outputs (figures, tables, files)" },
];

/** Optional reproducibility metadata. This build does not run or validate analyses. */
export function ReproEditor(props: { value: Record<string, string>; readOnly: boolean; onSave(v: Record<string, string>): void }) {
  const filled = FIELDS.filter((f) => props.value[f.key]).length;
  const [open, setOpen] = useState(filled > 0);
  const [form, setForm] = useState<Record<string, string>>(props.value);
  useEffect(() => setForm(props.value), [props.value]);
  const dirty = FIELDS.some((f) => (form[f.key] ?? "") !== (props.value[f.key] ?? ""));
  return (
    <div className="rounded-md border bg-background">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm">
        {open ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
        <span className="font-medium">Reproducibility details</span>
        <span className="text-muted-foreground">{filled ? `${filled} of ${FIELDS.length} recorded` : "None recorded"}</span>
      </button>
      {open && (
        <form
          className="grid gap-2 px-3 pb-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            const cleaned = Object.fromEntries(Object.entries(form).filter(([, v]) => v && v.trim()));
            props.onSave(cleaned);
          }}
        >
          <p className="text-xs text-muted-foreground sm:col-span-2">Optional details. BrainLog stores them but does not open or check the links.</p>
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label htmlFor={`repro-${f.key}`} className="text-xs">
                {f.label}
              </Label>
              {props.readOnly && f.url && props.value[f.key] ? (
                <a className="block break-all text-sm underline" href={props.value[f.key]} target="_blank" rel="noopener noreferrer nofollow">
                  {props.value[f.key]}
                </a>
              ) : (
                <Input
                  id={`repro-${f.key}`}
                  className="h-8"
                  disabled={props.readOnly}
                  value={form[f.key] ?? ""}
                  placeholder={f.url ? "https://…" : "Not recorded"}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                />
              )}
            </div>
          ))}
          {!props.readOnly && (
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" size="sm" disabled={!dirty}>
                Save reproducibility details
              </Button>
              {dirty && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setForm(props.value)}>
                  Discard changes
                </Button>
              )}
            </div>
          )}
        </form>
      )}
    </div>
  );
}
